import { type BaseEntity, Collection } from '@mikro-orm/core';
import { Entity, ManyToOne, OneToMany } from '@mikro-orm/decorators/legacy';
import type { Constructor } from '@wener/utils';
import { Feature } from '../../Feature';
import { EntityFeature } from '../enum';
import { resolveEntityRef } from '../resolveEntityRef';
import type { IdentifiableEntity } from '../types';
import type { IsHierarchyEntity } from './types';

export function withHierarchyEntity<E extends IsHierarchyEntity<any>>(
	f: () => Constructor<E>,
): <O extends BaseEntity & IdentifiableEntity, TBase extends Constructor<O>>(
	Base: TBase,
) => TBase & Constructor<IsHierarchyEntity<E>> {
	return <O extends BaseEntity & IdentifiableEntity, TBase extends Constructor<O>>(
		Base: TBase,
	): TBase & Constructor<IsHierarchyEntity<E>> => {
		@Feature([EntityFeature.IsHierarchy])
		@Entity({ abstract: true })
		class IsHierarchyMixinEntity
			extends (Base as Constructor<BaseEntity & IdentifiableEntity>)
			implements IsHierarchyEntity<E>
		{
			@ManyToOne(f, { nullable: true })
			parent?: E;

			@OneToMany(f, (e) => e.parent)
			children = new Collection<E>(this);

			get parentId() {
				return this.parent?.id;
			}

			set parentId(id: string | undefined) {
				this.parent = resolveEntityRef(f(), id).unwrap();
			}
		}

		return IsHierarchyMixinEntity as TBase & Constructor<IsHierarchyEntity<E>>;
	};
}
